import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, model, OnInit, signal, viewChild, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import * as _ from "underscore";

import {
  faPrint,
  faRankingStar,
  faPieChart,
  faCheckCircle,
  faCircleMinus,
  faFileExcel,
  faSpinner,
  faCircleExclamation,
  faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';

import { SchoolClass } from '../../../../core/models/school-class.model';
import { District } from '../../../../core/models/district.model';
import { School } from '../../../../core/models/school.model';
import { ManagementUser } from '../../../../core/models/management-user.model';
import { SessionStorageService } from '../../../../core/services/session-storage.service';
import { SubscriptionTypes } from '../../../../core/models/subscription-types.model';
import { StudentSession } from '../../../../core/models/student-session.model';
import { UtilityService } from '../../../../core/services/utility.service';
import { StudentsService } from '../../../../core/services/students.service';
import { ReportsService } from '../../../../core/services/reports.service';
import { PrintService } from '../../../../core/services/print.service';
import { LoadingService } from '../../../../core/services/loading.service';
import { DownloadService } from '../../../../core/services/download.service';
import { Objective } from 'src/app/core/models/objective.model';
import * as Sentry from '@sentry/angular';
import { ScoreCutoffs } from '../../../../core/models/score-cutoffs.model';
import { Student } from 'src/app/core/models/student.model';

import { groupUsageProgressIntroSteps } from './group-usage-progress.intro-steps';
import { groupEngagementIntroSteps } from './group-engagement.intro-steps';
import { canViewCoTeacherFilter, canViewSchoolFilter } from 'src/app/reports/utils';
import { CATEGORY_ICONS } from 'src/app/shared/standards/icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { PreviousWeekUsageCategoryPipe } from './previous-week-usage-category.pipe';
import { ColorCategoryComponent } from 'src/app/shared/components/color-category/color-category.component';
import { RefreshButton } from '../../../../shared/components/refresh-button/refresh-button.component';
import { FormsModule } from '@angular/forms';
import { DatePipe, DecimalPipe, JsonPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { DisplayGradePipe } from 'src/app/core/pipes/display-grade.pipe';
import { StudentsFilterComponent, StudentsFilterOptions } from 'src/app/shared/components/students-filter/students-filter.component';
import { SchoolTeacherFilterComponent } from 'src/app/shared/components/school-teacher-filter/school-teacher-filter.component';
import { PreviousWeekLevelCategoryPipe } from './previous-week-level-category.pipe';
import { TooltipComponent } from 'src/app/shared/components/tooltip/tooltip.component';
import { TourComponent } from '../../../../shared/components/tour/tour.component';
import { SchoolTeacherFilterOptions } from '../../../../core/models/filters';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { finalize, startWith, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { MainContentComponent } from 'src/app/shared/components/main-content/main-content.component';
import { SortColumn, sortUpdate } from '../../../../core/utils/table';
import { StudentProgress } from 'src/app/core/models/student-progress.model';
import { StudentPlacementPipe } from 'src/app/shared/pipes/student-placement.pipe';
import { AssessmentTypeNames } from 'src/app/core/models/assessment-types.model';
import { ZonePipe } from 'src/app/shared/pipes/zone.pipe';
import { getStudentGrade } from 'src/app/core/utils/grade';
import { getZone } from 'src/app/core/utils/zone';
import { UserRoles } from 'src/app/core/models/user.model';

@Component({
  selector: 'wf-group-summary',
  templateUrl: './group.component.html',
  styleUrls: ['./group.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FontAwesomeModule,
    MainContentComponent,
    SchoolTeacherFilterComponent,
    StudentsFilterComponent,
    DisplayGradePipe,
    NgTemplateOutlet,
    RefreshButton,
    FormsModule,
    DecimalPipe,
    DatePipe,
    StudentPlacementPipe,
    ZonePipe,
    PreviousWeekLevelCategoryPipe,
    PreviousWeekUsageCategoryPipe,
    ColorCategoryComponent,
    TourComponent,
    TooltipComponent,
  ],
})
export class GroupSummaryComponent {
  private route = inject(ActivatedRoute);
  private readonly reportService = inject(ReportsService);
  private readonly printService = inject(PrintService);
  private readonly loadingService = inject(LoadingService);
  private readonly utilityService = inject(UtilityService);
  readonly currentUser = inject(SessionStorageService).userData;

  protected readonly refresh$ = new BehaviorSubject<void>(undefined);

  printContent = viewChild.required<ElementRef<HTMLElement>>('printContent');

  readonly groupUsageProgressIntroSteps = groupUsageProgressIntroSteps;
  readonly groupEngagementIntroSteps = groupEngagementIntroSteps;

  readonly DATE_FORMAT = "MM/dd/yyyy";
  readonly categoryIcon = CATEGORY_ICONS["class-reports"];
  printIcon = faPrint ;
  excelIcon = faFileExcel ;
  loadingIcon = faSpinner ;

  readonly COMMON_COLUMNS = {
    STUDENT_NAME: 'Student Name',
    GRADE: 'Grade',
    TEACHER_NAME: 'Teacher Name',
  }

  readonly USAGE_COLUMNS = {
    DATE_STARTED: 'Date Started',
    LAST_USAGE_DATE: 'Last Usage Date',
    CURRENT_PLACEMENT: 'Current Placement',
    PROGRESS: 'Progress',
    CURRENT_ZONE: 'Current Zone',
    AVERAGE_WEEKLY_USAGE: 'Avg Weekly Usage (min)',
    PREVIOUS_WEEK_USAGE: 'Previous Week Usage (min)',
    CURRENT_WEEK_USAGE: 'Current Week Usage (min)',
  } as const;

  readonly ENGAGEMENT_COLUMNS = {
    CURRENT_TEAM: 'Current Team',
    TOTAL_POINTS: 'Total Points',
    PREVIOUS_WEEK_POINTS: 'Previous Week Points',
    CURRENT_LEVEL: 'Current Level',
    CURRENT_WEEK_LEVELS_COMPLETED: 'Current Week Levels Completed',
    PREVIOUS_WEEK_LEVELS_COMPLETED: 'Previous Week Levels Completed',
  }

  readonly reportExporting = signal(false);
  readonly reportError = signal("");
  readonly hideSharedStudents = signal(true);
  readonly showTeacherColumn = signal(false);

  readonly showSchoolTeacherFilter = computed(() => canViewSchoolFilter(this.currentUser()));
  readonly showCoTeacherFilter = computed(() => canViewCoTeacherFilter(this.currentUser()));

  readonly reportType = signal<"usage" | "engagement" > ("usage");

  readonly districts = signal<District[]>([]);
  readonly schools = signal<School[]>([]);
  readonly teachers = signal<ManagementUser[]>([]);

  // applied to at the request level
  readonly schoolTeacherFilter = signal<SchoolTeacherFilterOptions | undefined>(undefined);
  // applied at the UI level
  readonly studentsFilter = signal<StudentsFilterOptions | undefined>(undefined);
  readonly zoneFilter = signal<string>('All');

  readonly schoolTeacher = computed(() => {
    const user = this.currentUser();
    if (!user) {
      return undefined;
    }
    if (user.userRole === UserRoles.TEACHER || user.effectiveUserRole === UserRoles.TEACHER) {
      return {
        schoolID: user.schoolID,
        teacherID: user.userID,
      }
    }

    const filter = this.schoolTeacherFilter();
    if (!filter || !filter.school || !filter.teacher) {
      return undefined;
    }
    return {
      schoolID: filter.school.schoolID,
      teacherID: filter.teacher.userID,
    }
  });

  // TODO: make linked signal to reset when reportType changes
  protected readonly sortEffect = effect(() => {
    this.reportType();
    this.sortColumn.set({
      column: this.COMMON_COLUMNS.STUDENT_NAME,
      reverse: false,
    })
  })

  readonly sortColumn = signal<SortColumn>({
    column: this.COMMON_COLUMNS.STUDENT_NAME,
    reverse: false,
  });

  readonly studentProgresses = toSignal(
    combineLatest([toObservable(this.schoolTeacher), this.refresh$]).pipe(
      tap(() => this.loadingService.start()),
      switchMap(([schoolTeacher]) =>
        (
          !schoolTeacher
            ? of([])
            : this.reportService.getGroupSummaryReportData() // TODO: make this accept values instead of reading from session storage
        ).pipe(
          finalize(() => this.loadingService.finish()) // finish here to account for switchMap canceling request
        )
      ),
    ),
    { initialValue: [] }
  );

  readonly grades = computed(() => {
    const progresses = this.studentProgresses();
    return this.utilityService.sortGrades(Array.from(new Set(progresses.map(p => getStudentGrade(p.grade)))));
  });

  readonly classes = computed(() => {
    const progresses = this.studentProgresses();
    const classes = Object.values(progresses.reduce((a: { [key: number]: SchoolClass}, p) => {
      if (p.schoolClassID) {
        a[p.schoolClassID] = {
          schoolClassID: p.schoolClassID,
          name: p.schoolClassName,
          teacherID: p.teacherID,
          createDate: new Date(),
          status: ''
        }
      }
      return a;
    }, {})).sort((c1, c2) => c1.name.localeCompare(c2.name));
    return [{ schoolClassID: 0, name: 'All' } as SchoolClass].concat(classes);
  });

  readonly studentProgressesFiltered = computed(() => this.studentProgresses().filter(p => {
    const studentsFilter = this.studentsFilter();
    if (studentsFilter) {
      const grade = getStudentGrade(p.grade);
      if (isNaN(parseInt(studentsFilter.grade))) {
        if (
          (studentsFilter.grade === 'four_below_all' && p.grade > 5)
          || (studentsFilter.grade === 'five_up_all' && p.grade < 6)
          || (studentsFilter.grade === 'K' && grade !== 'K')
          || (studentsFilter.grade === 'Other' && grade !== 'Other')
        ) {
          return false;
        }
      } else if (studentsFilter.grade !== grade) {
        return false;
      }

      if (
        (studentsFilter.extraSupport === 'Yes' && !p.extraSupportNeeded)
        || (studentsFilter.extraSupport === 'No' && p.extraSupportNeeded)
      ) {
        return false;
      }

      const classID = parseInt(studentsFilter.class);
      if (classID && classID !== p.schoolClassID) {
        return false;
      }
    }

    // TODO: figure out a better way to do this zone business
    const zoneFilter = this.zoneFilter();
    const zone = getZone(p.grade, p.latestSystemScore);
    if (
      (zoneFilter === 'None' && p.latestSystemScore)
      || (zoneFilter !== 'All' && zone.label !== zoneFilter)
    ) {
      return false;
    }

    const teacherID = this.reportService.getSelectedTeacherForReports()?.userID;
    return (
      !this.hideSharedStudents()
      || !teacherID
      || p.teacherID === teacherID
    );
  }));

  readonly studentProgressesFilteredSorted = computed(() =>
    [...this.studentProgressesFiltered()].sort((s1, s2) => this.studentProgressComparator(s1, s2))
  );

  constructor() {
    const user = this.currentUser();
    if (user) {
      this.hideSharedStudents.set(!user.isTeacherUser());
    }
    this.districts.set(this.route.snapshot.data['filterData'].districts);
    this.schools.set(this.route.snapshot.data['filterData'].schools);
    this.teachers.set(this.route.snapshot.data['filterData'].teachers);

    if (user && (user.isFILUser() || user.isDistrictUser()))
    {
      // The FIL user and District user roles can select all schools
      this.schools.update(s => [{ schoolID: 0, districtID: 0, name: 'All', enabled: true }].concat(s));
    }

    if (canViewSchoolFilter(this.currentUser()))
    {
      // The FIL user and District and School user roles can select all teachers
      this.teachers.update(t => [ManagementUser.getGenericUser()].concat(t));
    }

  }

  private studentProgressComparator(s1: StudentProgress, s2: StudentProgress) {
    const { column, reverse } = this.sortColumn();

    const nameComp = s1.firstName.localeCompare(s2.firstName) || s1.lastName.localeCompare(s2.lastName);

    let comp = nameComp;
    if (column === this.COMMON_COLUMNS.GRADE) {
      comp = s1.grade - s2.grade;
    } else if (column === this.COMMON_COLUMNS.TEACHER_NAME) {
      comp = s1.teacherFirstName.localeCompare(s2.teacherFirstName) || s1.teacherLastName.localeCompare(s2.teacherLastName);
    } else if (column === this.USAGE_COLUMNS.DATE_STARTED) {
      comp = (s1.startDateTime?.getTime() ?? 0) - (s2.startDateTime?.getTime() ?? 0);
    } else if (column === this.USAGE_COLUMNS.LAST_USAGE_DATE) {
      comp = (s1.lastUsageDateTime?.getTime() ?? 0) - (s2.lastUsageDateTime?.getTime() ?? 0);
    } else if (column === this.USAGE_COLUMNS.PROGRESS || column === this.ENGAGEMENT_COLUMNS.CURRENT_LEVEL) {
      comp = s1.levelsCompleted - s2.levelsCompleted;
    } else if (column === this.USAGE_COLUMNS.CURRENT_PLACEMENT) {
      const s1IsComplete = s1.highestCompletedAssessmentType === AssessmentTypeNames.POSTDIAGNOSTIC && s1.levelsCompleted === 120;
      const s2IsComplete = s2.highestCompletedAssessmentType === AssessmentTypeNames.POSTDIAGNOSTIC && s2.levelsCompleted === 120;
      // We want to place MIDDIAGOSTIC in between Unit 12 and Unit 13
      // So level effectively level 60.5
      const midDiagnosticEffectiveLevel = 5 * 12 + 0.5

      if (s1IsComplete && !s2IsComplete) {
        comp = 1;
      } else if (!s1IsComplete && s2IsComplete) {
        comp = -1;
      } else if (s1.activeAssessmentType === AssessmentTypeNames.MIDDIAGNOSTIC && s2.activeAssessmentType === AssessmentTypeNames.INTERVENTION) {
        // This approach only works because INTERVENTION and MIDDIAGOSTIC are next to each other numerically (AssessmentType 1 and 2)
        comp = midDiagnosticEffectiveLevel - s2.levelsCompleted;
      } else if (s1.activeAssessmentType === AssessmentTypeNames.INTERVENTION && s2.activeAssessmentType === AssessmentTypeNames.MIDDIAGNOSTIC) {
        comp = s1.levelsCompleted - midDiagnosticEffectiveLevel;
      } else {
        // Otherwise sort everything else normally
        comp = (s1.activeAssessmentType - s2.activeAssessmentType) || (s1.levelsCompleted - s2.levelsCompleted);
      }
    } else if (column === this.USAGE_COLUMNS.CURRENT_ZONE) {
      comp = s1.latestSystemScore - s2.latestSystemScore;
    } else if (column === this.USAGE_COLUMNS.AVERAGE_WEEKLY_USAGE) {
      comp = s1.averageWeeklySessionSeconds - s2.averageWeeklySessionSeconds;
    } else if (column === this.USAGE_COLUMNS.PREVIOUS_WEEK_USAGE) {
      comp = s1.previousWeekSessionSeconds - s2.previousWeekSessionSeconds;
    } else if (column === this.USAGE_COLUMNS.CURRENT_WEEK_USAGE) {
      comp = s1.currentWeekSessionSeconds - s2.currentWeekSessionSeconds;
    } else if (column === this.ENGAGEMENT_COLUMNS.CURRENT_TEAM) {
      comp = (s1.currentTeamName ?? '').localeCompare(s2.currentTeamName ?? '');
    } else if (column === this.ENGAGEMENT_COLUMNS.TOTAL_POINTS) {
      comp = s1.totalPoints - s2.totalPoints;
    } else if (column === this.ENGAGEMENT_COLUMNS.PREVIOUS_WEEK_POINTS) {
      comp = s1.previousWeekPoints - s2.previousWeekPoints;
    } else if (column === this.ENGAGEMENT_COLUMNS.CURRENT_LEVEL) {
      comp = s1.levelsCompleted - s2.levelsCompleted;
    } else if (column === this.ENGAGEMENT_COLUMNS.CURRENT_WEEK_LEVELS_COMPLETED) {
      comp = s1.weeklyLevelsCompleted - s2.weeklyLevelsCompleted;
    } else if (column === this.ENGAGEMENT_COLUMNS.PREVIOUS_WEEK_LEVELS_COMPLETED) {
      comp = s1.previousWeeklyLevelsCompleted - s2.previousWeeklyLevelsCompleted;
    }


    if (comp === 0 && column !== this.COMMON_COLUMNS.STUDENT_NAME) {
      // On tie of non-name columns, fall back to ascending name
      // This ignores reverse. Reverse should only affect the top level sort, not the tiebreaker.
      return nameComp;
    }

    return reverse ? (comp * -1) : comp
  }

  updateSort(clickedColumn: string) {
    this.sortColumn.update(sortUpdate(clickedColumn));
  }

  printSummary() {
    const school = this.reportService.getSelectedSchoolForReports().name;
    const teacher = this.reportService.getSelectedTeacherForReports();
    const teacherName = `${teacher.firstName} ${teacher.lastName}`;
    console.log('printSummary', this.printContent());
    this.printService.openPrintWindow(this.printContent().nativeElement, school, teacherName);
  }

  exportGroupSummary() {
    this.reportExporting.set(true);
    this.reportService.downloadGroupSummary(this.reportService.getSelectedSchoolForReports().schoolID, this.reportService.getSelectedTeacherForReports().userID, this.showTeacherColumn())
      .subscribe({
        next: (blob: Blob) => {
          const a = document.createElement('a')
          const objectUrl = URL.createObjectURL(blob)
          a.href = objectUrl
          a.download = 'group_summary.xlsx';
          a.click();
          URL.revokeObjectURL(objectUrl);
          this.reportExporting.set(false);
        },
        error: (err: any) => {
          this.reportExporting.set(false);
          this.reportError.set(`There was an error trying to export the Group Summary report. Please contact support for assistance.`);

          Sentry.captureException(err, {
            tags: {
              section: 'reports',
              report: 'system-summary-group',
              action: 'export',
              districtId: this.reportService.getSelectedDistrictForReports().districtID,
              schoolId: this.reportService.getSelectedSchoolForReports().schoolID,
              teacherId: this.reportService.getSelectedTeacherForReports().userID,
            }
          }) ;
        }
      }) ;
  }
}
